import { Suspense } from 'react'
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router-dom'

import {
  BenefitsPage,
  DaysOffPage,
  HomePage,
  KudosDetailPage,
  KudosFeedPage,
  Layout,
  NewsDetailsPage,
  NewsletterDetailsPage,
  NewslettersPage,
  NotificationRedirectPage,
  NotificationsPage,
  NewsPage,
  NotFoundPage,
  OidcCallbackPage,
  OnBoardingDetailsPage,
  OnBoardingPage,
  PeoplePage,
  PeopleSpotlightDetailsPage,
  PeopleSpotlightsPage,
  OrgStructurePage,
  PeopleUpdatesPage,
  PersonDetailPage,
  PoliciesPage,
  ProfilePage,
  ResourcesPage,
  ResourcesPageDetails,
  SignInPage,
  WorkingHoursPage
} from './lazyPages'
import { paths } from './paths'
import {
  LoadingFallback,
  ProtectedOutlet,
  RootLayout,
  ScrollToTop
} from './routeComponents'

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      { path: paths.auth.login, element: <SignInPage /> },
      { path: paths.auth.callback, element: <OidcCallbackPage /> },
      {
        element: <ProtectedOutlet />,
        children: [
          {
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ScrollToTop />
                <Layout>
                  <Outlet />
                </Layout>
              </Suspense>
            ),
            children: [
              {
                path: paths.home,
                element: <HomePage />,
                loader: () =>
                  import('src/features/home/homeLoader').then((m) =>
                    m.homeLoader()
                  )
              },
              { path: paths.profile, element: <ProfilePage /> },
              {
                path: paths.people.root,
                element: <PeoplePage />,
                loader: () =>
                  import('src/features/people/loaders/peopleLoader').then((m) =>
                    m.peopleLoader()
                  )
              },
              {
                path: paths.people.directory,
                element: <PeoplePage />,
                loader: () =>
                  import('src/features/people/loaders/peopleLoader').then((m) =>
                    m.peopleLoader()
                  )
              },
              {
                path: paths.people.orgStructure,
                element: <OrgStructurePage />,
                loader: () =>
                  import('src/features/people/loaders/orgStructureLoader').then(
                    (m) => m.orgStructureLoader()
                  )
              },
              {
                path: paths.people.personDetail(':id'),
                element: <PersonDetailPage />,
                loader: (args) =>
                  import('src/features/people/loaders/personDetailLoader').then(
                    (m) => m.personDetailLoader(args)
                  )
              },
              {
                path: paths.people.spotlights,
                element: <PeopleSpotlightsPage />,
                loader: () =>
                  import('src/features/people/loaders/peopleSpotlightsLoader').then(
                    (m) => m.peopleSpotlightsLoader()
                  )
              },
              {
                path: paths.people.updates,
                element: <PeopleUpdatesPage />,
                loader: () =>
                  import('src/features/people-updates/peopleUpdatesLoader').then(
                    (m) => m.peopleUpdatesLoader()
                  )
              },
              {
                path: paths.people.spotlightDetail(':id'),
                element: <PeopleSpotlightDetailsPage />,
                loader: (args) =>
                  import('src/features/people/loaders/peopleSpotlightDetailLoader').then(
                    (m) => m.peopleSpotlightDetailLoader(args)
                  )
              },
              {
                path: paths.news.root,
                element: <NewsPage />,
                loader: () =>
                  import('src/features/news/newsLoader').then((m) =>
                    m.newsLoader()
                  )
              },
              {
                path: paths.news.detail(':id'),
                element: <NewsDetailsPage />,
                loader: (args) =>
                  import('src/features/news/newsDetailLoader').then((m) =>
                    m.newsDetailLoader(args)
                  )
              },
              {
                path: paths.policies,
                element: <PoliciesPage />,
                loader: () =>
                  import('src/features/policies/policiesLoader').then((m) =>
                    m.policiesLoader()
                  )
              },
              {
                path: paths.benefits,
                element: <BenefitsPage />,
                loader: () =>
                  import('src/features/benefits/benefitsLoader').then((m) =>
                    m.benefitsLoader()
                  )
              },
              {
                path: paths.daysOff,
                element: <DaysOffPage />,
                loader: () =>
                  import('src/features/days-off/daysOffLoader').then((m) =>
                    m.daysOffLoader()
                  )
              },
              {
                path: paths.daysOffWorkingHours,
                element: <WorkingHoursPage />,
                loader: () =>
                  import('src/features/days-off/workingHoursLoader').then((m) =>
                    m.workingHoursLoader()
                  )
              },
              {
                path: paths.resources.root,
                element: <ResourcesPage />,
                loader: () =>
                  import('src/features/resources/resourcesLoader').then((m) =>
                    m.resourcesLoader()
                  )
              },
              {
                path: paths.resources.page(':slug'),
                element: <ResourcesPageDetails />,
                loader: (args) =>
                  import('src/features/resources/resourcesDetailLoader').then(
                    (m) => m.resourcesDetailLoader(args)
                  )
              },
              {
                path: paths.onBoarding.root,
                element: <OnBoardingPage />,
                loader: () =>
                  import('src/features/onboarding/onBoardingLoader').then((m) =>
                    m.onBoardingLoader()
                  )
              },
              {
                path: paths.onBoarding.page(':slug'),
                element: <OnBoardingDetailsPage />,
                loader: (args) =>
                  import('src/features/onboarding/onBoardingDetailLoader').then(
                    (m) => m.onBoardingDetailLoader(args)
                  )
              },
              {
                path: paths.kudos.root,
                element: <KudosFeedPage />,
                loader: () =>
                  import('src/features/kudos/loaders/kudosFeedLoader').then(
                    (m) => m.kudosFeedLoader()
                  )
              },
              {
                path: paths.kudos.detail(':id'),
                element: <KudosDetailPage />,
                loader: (args) =>
                  import('src/features/kudos/loaders/kudosDetailLoader').then(
                    (m) => m.kudosDetailLoader(args)
                  )
              },
              {
                path: paths.notifications,
                element: <NotificationsPage />
              },
              {
                // Email links land here: mark read, then forward to the content.
                path: '/notifications/:notificationId',
                element: <NotificationRedirectPage />
              },
              {
                path: paths.newsletters.root,
                element: <NewslettersPage />,
                loader: () =>
                  import('src/features/newsletters/newslettersLoader').then(
                    (m) => m.newslettersLoader()
                  )
              },
              {
                path: paths.newsletters.detail(':id'),
                element: <NewsletterDetailsPage />,
                loader: () =>
                  import('src/features/newsletters/newslettersLoader').then(
                    (m) => m.newslettersLoader()
                  )
              },
              { path: '*', element: <NotFoundPage /> }
            ]
          }
        ]
      }
    ]
  }
]

export const router = createBrowserRouter(routes)
